import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LogOut, Diamond, Gem, ShieldCheck, Clock, ArrowUpRight } from "lucide-react";
import { Reveal, StatusBadge } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [diamonds, setDiamonds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([api.get("/diamonds"), api.get("/portal/summary")]);
      setDiamonds(d.data); setSummary(s.data);
      if (user?.role === "admin") {
        try { const a = await api.get("/admin/applications"); setApplications(a.data); }
        catch (err) { /* applications are optional for non-admins */ }
      }
    } catch (err) { /* dashboard data load is non-blocking */ }
    finally { setLoading(false); }
  }, [user]);

  const decide = async (uid, action) => {
    try {
      await api.patch(`/admin/users/${uid}/${action}`);
      setApplications((p) => p.filter((x) => x.id !== uid));
      toast.success(`Application ${action === "activate" ? "activated" : "rejected"}`);
    } catch { toast.error("Action failed"); }
  };
  useEffect(() => { load(); }, [load]);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="pt-28 pb-24 mx-auto max-w-[1400px] px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <div className="label-eyebrow">{user?.role === "admin" ? "Administration" : "Industry Portal"}</div>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl">{user?.company || user?.name}</h1>
          <p className="text-sm text-bb-silver/55 mt-2">Signed in as {user?.email} · {user?.role}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/portal/submit?kind=rough" data-testid="portal-register-rough-btn" className="btn-ghost rounded-full px-5 py-3 text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" strokeWidth={1.6} /> Register Rough
          </Link>
          <Link to="/portal/submit?kind=polished" data-testid="portal-submit-btn" className="btn-gold rounded-full px-5 py-3 text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" strokeWidth={1.6} /> Submit Polished
          </Link>
          <button onClick={doLogout} data-testid="portal-logout-btn" className="btn-ghost rounded-full px-5 py-3 text-sm flex items-center gap-2">
            <LogOut className="h-4 w-4" strokeWidth={1.4} /> Sign out
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mb-10">
        <Stat icon={Diamond} label="Total diamonds" value={summary?.total ?? 0} />
        <Stat icon={ShieldCheck} label="Verified / Released" value={summary?.verified ?? 0} />
        <Stat icon={Clock} label="In progress" value={summary?.in_progress ?? 0} />
      </div>

      {user?.role === "admin" && applications.length > 0 && (
        <Reveal>
          <div className="glass rounded-2xl overflow-hidden mb-10">
            <div className="px-6 py-5 border-b border-white/8">
              <span className="label-eyebrow">Pending applications</span>
            </div>
            <div className="divide-y divide-white/6">
              {applications.map((a) => (
                <div key={a.id} data-testid={`application-${a.id}`} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div>
                    <div className="text-bb-white">{a.company} <span className="text-bb-silver/50">· {a.name}</span></div>
                    <div className="text-xs text-bb-silver/50 font-mono">{a.email} · Licence {a.licence_number || "—"}</div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`activate-${a.id}`} onClick={() => decide(a.id, "activate")} className="btn-gold rounded-full px-5 py-2 text-xs">Activate</button>
                    <button data-testid={`reject-${a.id}`} onClick={() => decide(a.id, "reject")} className="btn-ghost rounded-full px-5 py-2 text-xs">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      <Reveal>
        <div className="glass rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-white/8 flex items-center gap-2">
            <Gem className="h-4 w-4 text-bb-gold" strokeWidth={1.2} />
            <span className="label-eyebrow">Your diamonds</span>
          </div>
          {loading ? (
            <div className="p-12 text-center text-bb-silver/50">Loading…</div>
          ) : diamonds.length === 0 ? (
            <div className="p-16 text-center">
              <p className="text-bb-silver/60">No diamonds submitted yet.</p>
              <Link to="/portal/submit" className="mt-5 inline-block btn-gold rounded-full px-6 py-3 text-sm">Submit your first diamond</Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="diamonds-table">
                <thead>
                  <tr className="text-left text-bb-silver/45 text-xs uppercase tracking-wider">
                    <th className="px-6 py-4 font-normal">Reference</th>
                    <th className="px-6 py-4 font-normal">Diamond</th>
                    <th className="px-6 py-4 font-normal">Specs</th>
                    <th className="px-6 py-4 font-normal">Status</th>
                    <th className="px-6 py-4 font-normal">BB Number</th>
                    <th className="px-6 py-4 font-normal"></th>
                  </tr>
                </thead>
                <tbody>
                  {diamonds.map((d) => (
                    <tr
                      key={d.id}
                      data-testid={`diamond-row-${d.id}`}
                      onClick={() => navigate(`/portal/diamond/${d.id}`)}
                      className="border-t border-white/6 hover:bg-white/[0.02] cursor-pointer transition-colors duration-300"
                    >
                      <td className="px-6 py-4 font-mono text-xs text-bb-silver/70">{d.reference}</td>
                      <td className="px-6 py-4 text-bb-white">{d.name}</td>
                      <td className="px-6 py-4 text-bb-silver/70">{d.carat}ct · {d.shape} · {d.color}/{d.clarity}</td>
                      <td className="px-6 py-4"><StatusBadge status={d.status} label={d.status} /></td>
                      <td className="px-6 py-4 font-mono text-xs text-bb-gold">{d.bb_number || "Pending"}</td>
                      <td className="px-6 py-4 text-right"><ArrowUpRight className="h-4 w-4 text-bb-silver/40 inline" strokeWidth={1.4} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Reveal>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-2xl p-6 flex items-center gap-5">
      <div className="grid place-items-center h-12 w-12 rounded-full border border-white/10 bg-white/[0.03]">
        <Icon className="h-5 w-5 text-bb-gold" strokeWidth={1.2} />
      </div>
      <div>
        <div className="font-serif text-3xl">{value}</div>
        <div className="label-eyebrow mt-1">{label}</div>
      </div>
    </div>
  );
}
